import { Tab, TabItem } from '@/types';

export type TabTotalsOrgSnapshot = {
  settings?: {
    serviceCharge?: { enabled?: boolean };
    iva?: { enabled?: boolean };
  };
  serviceChargeRate?: number;
  ivaRate?: number;
};

export type TabSaleTotals = {
  subtotal: number;
  discount: number;
  discountAmount: number;
  subtotalAfterDiscount: number;
  serviceCharge: number;
  serviceChargeRate?: number;
  iva: number;
  ivaRate?: number;
  tip: number;
  total: number;
};

export function calculateItemTotal(item: TabItem): number {
  const baseTotal = item.unitPrice * item.quantity;
  return baseTotal - (baseTotal * item.discount) / 100;
}

/** A line with its own % does not also receive the tab-level discount. */
export function itemOverridesTabDiscount(item: Pick<TabItem, 'discount'>): boolean {
  return Number(item.discount) > 0;
}

export function calculateTabSubtotal(tab: Tab): number {
  return (tab.items ?? []).reduce((sum, item) => sum + calculateItemTotal(item), 0);
}

export function calculateTabDiscountAmount(tab: Pick<Tab, 'discount' | 'items'>): number {
  const percent = Number(tab.discount) || 0;
  if (percent <= 0) return 0;

  const eligibleSubtotal = (tab.items ?? []).reduce((sum, item) => {
    if (itemOverridesTabDiscount(item)) return sum;
    return sum + calculateItemTotal(item);
  }, 0);

  return eligibleSubtotal * (percent / 100);
}

function calculatePercentageCharge(
  subtotal: number,
  settingKey: 'serviceCharge' | 'iva',
  rateKey: 'serviceChargeRate' | 'ivaRate',
  org?: TabTotalsOrgSnapshot | null,
  tab?: Tab,
): number {
  const settings = org?.settings?.[settingKey];
  const rate = org?.[rateKey];

  if (settingKey === 'serviceCharge' && tab?.serviceChargeDisabled) {
    return 0;
  }

  if (settings?.enabled && rate) {
    return subtotal * (rate / 100);
  }
  return 0;
}

export function calculateServiceChargeAmount(
  subtotalAfterDiscount: number,
  tab: Tab,
  org?: TabTotalsOrgSnapshot | null,
): number {
  return calculatePercentageCharge(subtotalAfterDiscount, 'serviceCharge', 'serviceChargeRate', org, tab);
}

export function calculateIvaAmount(
  subtotalAfterDiscount: number,
  tab: Tab,
  org?: TabTotalsOrgSnapshot | null,
): number {
  if (tab.ivaDisabled) return 0;
  return calculatePercentageCharge(subtotalAfterDiscount, 'iva', 'ivaRate', org);
}

export function getServiceChargeSettings(org?: TabTotalsOrgSnapshot | null) {
  const settings = org?.settings?.serviceCharge;
  const rate = org?.serviceChargeRate;
  return {
    enabled: settings?.enabled || false,
    rate: rate || 0,
  };
}

export function getIvaSettings(org?: TabTotalsOrgSnapshot | null) {
  const settings = org?.settings?.iva;
  const rate = org?.ivaRate;
  return {
    enabled: settings?.enabled || false,
    rate: rate || 0,
  };
}

export function toTabTotalsOrgSnapshot(
  org?: {
    settings?: TabTotalsOrgSnapshot['settings'];
    serviceChargeRate?: number;
    ivaRate?: number;
  } | null,
): TabTotalsOrgSnapshot | undefined {
  if (!org) return undefined;
  return {
    settings: org.settings,
    serviceChargeRate: org.serviceChargeRate,
    ivaRate: org.ivaRate,
  };
}

export function calculateTabSaleTotals(
  tab: Tab,
  org?: TabTotalsOrgSnapshot | null,
): TabSaleTotals {
  const subtotal = calculateTabSubtotal(tab);
  const discount = tab.discount;
  const discountAmount = calculateTabDiscountAmount(tab);
  const subtotalAfterDiscount = subtotal - discountAmount;
  const serviceChargeSettings = getServiceChargeSettings(org);
  const ivaSettings = getIvaSettings(org);
  const serviceCharge = calculateServiceChargeAmount(subtotalAfterDiscount, tab, org);
  const iva = calculateIvaAmount(subtotalAfterDiscount, tab, org);
  const tip = tab.tip ?? 0;
  const total = subtotalAfterDiscount + serviceCharge + iva + tip;

  return {
    subtotal,
    discount,
    discountAmount,
    subtotalAfterDiscount,
    serviceCharge,
    serviceChargeRate:
      serviceChargeSettings.enabled && serviceCharge > 0 ? serviceChargeSettings.rate : undefined,
    iva,
    ivaRate: ivaSettings.enabled && iva > 0 ? ivaSettings.rate : undefined,
    tip,
    total,
  };
}
